import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import { NO_DATA_AVAILABLE } from '../../../constants/common'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, downloadFileBlob, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {AppsContext, SELECT_APP, SET_APPS_LIST} from './context/context'
import AppsTable from './AppsTable'
import AppFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const Apps = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(AppsContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/oauth/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getAppsListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'apps.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_APPS_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'apps.loadError' }))
    }
  }

  useEffect(() => {
    getAppsListData()
  }, [])

  useEffect(() => {
    const url = `/api/oauth/?search=${search}`
    currentUrl.current = url
    getAppsListData()
  }, [debouncedSearch])

  const deleteApp = async (record) => {
    let res = await deleteRequest(`/api/oauth/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'apps.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getAppsListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'apps.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'apps.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'apps.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteApp(record),
      centered: true,
      maskClosable: true,
    })
  }

  const downloadCredentials = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'apps.downloadCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/oauth/download/${record.id}/${record.client_id}/`,
      true,
      true
    )
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    // Was `if (res?.msg)`, which on a failure carrying no message fell through
    // and wrote the error object out as a .csv. Matches the guard already used
    // by the shifts, plants and test_zones exports.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type:
            res?.code === NO_DATA_AVAILABLE ? 'info' : 'error',
          msg: intl.formatMessage({
            id:
              res?.code === NO_DATA_AVAILABLE
                ? 'generic.noDataToExport'
                : 'apps.downloadError',
          }),
        },
      })
    } else {
      downloadFileBlob(res, `${record.name}-credentials.csv`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'apps.downloadCompleted' }),
        },
      })
    }
  }

  const onCreateApp = () => {
    dispatch({
      type: SELECT_APP,
      data: { selected_app: {}, show_app_popup: true },
    })
  }

  const onEditApp = (record) => {
    dispatch({
      type: SELECT_APP,
      data: { selected_app: record, show_app_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.apps_list?.length) return []
    return state.apps_list.map((item, index) => ({
      ...item,
      key: `app-${index}`,
      rowKey: `app-${item.id}`,
      index,
    }))
  }, [state.apps_list])

  return (
    <>
      {contextHolder}

      {state.show_app_popup && (
        <AppFormModal getAppsListData={getAppsListData} />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_apps"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'apps.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="search-apps"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button onClick={onCreateApp}>
                  {intl.formatMessage({ id: 'apps.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <AppsTable
              dataSource={dataSource}
              pagination={pagination}
              getAppsListData={getAppsListData}
              onEdit={onEditApp}
              onDelete={confirmDelete}
              onDownloadCredentials={downloadCredentials}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default Apps
